import { ChangeDetectionStrategy, Component, inject, signal, OnInit } from '@angular/core';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { ReactiveFormsModule, FormGroup, FormControl, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { HttpClient } from '@angular/common/http';
import {} from '@angular/common';
import { FormlyErrorComponent } from '../formly-error.component';
import { InfoTooltipComponent } from '../info-tooltip.component';
import { catchError, of, finalize, Subject, takeUntil } from 'rxjs';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { commitUserValue } from './user-edit.util';
import { ibanValidator } from '../../validators/iban';

interface OpenIbanResponse {
  valid: boolean;
  messages: string[];
  iban: string;
  bankData?: {
    bankCode: string;
    name: string;
    bic: string;
  };
  checkResults: any;
}

@Component({
  selector: 'formly-iban-bic',
  template: `
    <div [formGroup]="ibanBicForm" class="flex flex-col">
      <div class="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3 w-full">
        <mat-form-field
          class="dd-compact-field w-full sm:flex-1 min-w-0"
          [class.mat-form-field-invalid]="ibanControl.invalid && ibanControl.touched">
          <mat-label>
            IBAN
            <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
          </mat-label>
          <input
            matInput
            formControlName="iban"
            [required]="props.required"
            [formlyDataCy]="field"
            (blur)="onIbanBlur()"
            (keydown.enter)="onEnter($event)"
            placeholder="Voer IBAN-nummer in" />

          <!-- IBAN Validation Status -->
          <span matSuffix>
            @if (ibanControl.value && !isLoadingBic() && ibanVerified()) {
              <mat-icon class="text-primary icon-size-5">check_circle</mat-icon>
            } @else if (ibanControl.value && !isLoadingBic() && ibanControl.errors?.['invalidIban']) {
              <mat-icon class="text-warn icon-size-5">error</mat-icon>
            }
          </span>

          @if (ibanControl.errors?.['invalidIban']) {
            <mat-error> Voer een geldig IBAN-nummer in </mat-error>
          }
        </mat-form-field>
        <!-- dd-button-loading: the shared loading overlay from handoff #20830 (a stronger
             wash on the base fill) while the BIC lookup is in flight. -->
        <button
          mat-stroked-button
          type="button"
          class="dd-verify-button shrink-0 self-start"
          [class.dd-button-loading]="isLoadingBic()"
          [attr.aria-busy]="isLoadingBic() || null"
          [disabled]="isLoadingBic() || ibanControl.invalid || !ibanControl.value || ibanControl.disabled"
          (click)="lookupBic()">
          <span class="dd-verify-content">
            @if (isLoadingBic()) {
              <mat-spinner class="dd-verify-spinner" diameter="18"></mat-spinner>
            }
            <span class="dd-verify-label">BIC ophalen</span>
          </span>
        </button>
      </div>

      @if (bicVisible()) {
        <!-- dd-compact-field keeps this field in the shared 37px/12px compact geometry,
             matching the IBAN field above it. -->
        <mat-form-field class="dd-compact-field" [class.mat-form-field-invalid]="bicControl.invalid && bicControl.touched">
          <mat-label>
            BIC
            <app-info-tooltip [tooltip]="props.bicInfo"></app-info-tooltip>
          </mat-label>
          <input
            matInput
            formControlName="bic"
            [formlyDataCy]="field"
            [required]="props.required"
            [readonly]="bicReadonly()"
            placeholder="Voer BIC in" />

          <span matSuffix class="flex items-center gap-2">
            @if (bicControl.value && !bicControl.errors) {
              <mat-icon class="text-primary icon-size-5">check_circle</mat-icon>
              <span class="text-xs text-primary">Geldige BIC</span>
            } @else if (bicControl.value && bicControl.errors?.['invalidBic']) {
              <mat-icon class="text-warn icon-size-5">error</mat-icon>
              <span class="text-xs text-warn">Ongeldige BIC</span>
            }
          </span>

          @if (bicControl.errors?.['required']) {
            <mat-error> Voer een BIC in </mat-error>
          }
          @if (bicControl.errors?.['invalidBic']) {
            <mat-error> Voer een geldige BIC in (8 of 11 tekens) </mat-error>
          }
          @if (bicControl.errors?.['fetchError']) {
            <mat-error> BIC kon niet worden opgehaald. Vul handmatig in. </mat-error>
          }
          @if (bankName()) {
            <mat-hint class="text-sm">
              {{ bankName() }}
            </mat-hint>
          }
        </mat-form-field>
      }
      @if (field.props.description) {
        <mat-hint class="inline-block mb-2">{{ field.props.description }}</mat-hint>
      }
      <app-formly-error [field]="field"></app-formly-error>
    </div>
  `,
  styles: [
    `
      /* Fixed width so the button keeps the same size regardless of label length
         or loading state. Shares min-width 12rem with the KVK verify button so
         both render identically, matching the design. margin-top 2px centers the
         36px button within the ~40px field box (floating-label appearance). */
      .dd-verify-button {
        min-width: 12rem;
      }

      /* Lay the spinner inline, left of the label, on a single line. min-width
         absorbs the extra spinner width so the button keeps the same size. */
      .dd-verify-content {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        white-space: nowrap;
      }

      .dd-verify-spinner {
        flex: 0 0 auto;
      }

      /* Keep the label upright — the global '[disabled] *' rule (tailwind.scss)
         italicizes descendants of disabled elements, but the design has no
         italic. */
      .dd-verify-label {
        font-style: normal;
      }

      /* Beside-the-field layout only kicks in from the sm breakpoint (640px);
         below that the button stacks under the field (mobile design), where the
         alignment offset must not apply. margin-top 2px centers the 36px button
         within the ~40px field box. */
      @media (min-width: 640px) {
        .dd-verify-button {
          margin-top: 2px;
        }

        /* Label-placement "above" reserves --dd-label-above-reserve above the box for
           the external label (.labels-above in _forms.scss). Push the button down past
           it, plus the 2px that centres the 36px button in the ~40px box. */
        :host-context(.labels-above) .dd-verify-button {
          margin-top: calc(var(--dd-label-above-reserve, 28px) + 2px);
        }
      }
    `,
  ],
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule,
    MatButtonModule,
    FormlyErrorComponent,
    InfoTooltipComponent,
    MatProgressSpinner,
    FormlyDataCyDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyIbanBicComponent extends FieldType implements OnInit {
  private http = inject(HttpClient);
  destroy$ = injectDestroy();
  isLoadingBic = signal(false);
  bankName = signal<string | null>(null);
  bankCode = signal<string | null>(null);
  // BIC field only appears after a lookup; read-only when fetched successfully,
  // editable when the lookup failed (user fills it in manually).
  bicVisible = signal(false);
  bicReadonly = signal(false);
  // True only after a successful lookup — drives the IBAN "verified" check icon.
  ibanVerified = signal(false);
  readonly #lookupCancelled$ = new Subject<void>();

  ibanBicForm = new FormGroup({
    iban: new FormControl('', [Validators.required, ibanValidator]),
    bic: new FormControl('', [Validators.required, this.bicValidator()]),
  });

  get ibanControl() {
    return this.ibanBicForm.get('iban') as FormControl;
  }

  get bicControl() {
    return this.ibanBicForm.get('bic') as FormControl;
  }

  ngOnInit() {
    // Initialize form with existing values if any
    if (this.field.formControl.value) {
      this.ibanBicForm.patchValue(this.field.formControl.value);
    }

    // Editing an entry that already has a BIC: show it straight away and let the
    // user correct it manually (not a fresh lookup → editable, not read-only).
    if (this.field.formControl.value?.bic) {
      this.bicVisible.set(true);
      this.bicReadonly.set(false);
    }

    // Handle disabled state properly
    if (this.props.disabled) {
      this.ibanControl.disable();
      this.bicControl.disable();
    }

    // Watch for changes to props.disabled
    this.form.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.props.disabled && this.ibanControl.enabled) {
        this.ibanControl.disable();
        this.bicControl.disable();
      } else if (!this.props.disabled && this.ibanControl.disabled) {
        this.ibanControl.enable();
        this.bicControl.enable();
      }
    });

    // Changing the IBAN invalidates a previously looked-up BIC: hide and clear it
    // so the user looks it up again for the new number. Blur-formatting uses
    // emitEvent:false, so reformatting the same IBAN does not trigger this.
    this.ibanControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.ibanVerified.set(false);
      if (this.bicVisible()) {
        this.bicVisible.set(false);
        this.bicReadonly.set(false);
        this.bankName.set(null);
        this.bankCode.set(null);
        this.bicControl.reset('', { emitEvent: false });
        this.bicControl.setErrors(null);
        this.updateFormValue();
      }
    });

    // Update parent form when our form changes
    this.ibanBicForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateFormValue();
    });
  }

  onEnter(event: Event) {
    event.preventDefault();
    if (!this.isLoadingBic() && this.ibanControl.valid && this.ibanControl.value) {
      this.lookupBic();
    }
  }

  lookupBic() {
    // A disabled control reports invalid=false while still holding a value, so
    // read-only forms must be guarded explicitly.
    if (this.isLoadingBic() || this.ibanControl.disabled) {
      return;
    }

    const iban = this.ibanControl.value?.replace(/\s/g, '').toUpperCase();
    if (!iban || this.ibanControl.invalid) {
      return;
    }

    this.#lookupCancelled$.next();
    this.isLoadingBic.set(true);
    this.bicReadonly.set(false);
    this.ibanVerified.set(false);
    this.bicControl.setErrors(null);

    this.fetchBIC(iban)
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.#lookupCancelled$),
        finalize(() => this.isLoadingBic.set(false))
      )
      .subscribe({
        next: response => {
          // Reveal the BIC field now that a lookup has run.
          this.bicVisible.set(true);
          if (response?.bankData?.bic) {
            // Looked up successfully → fill and lock the BIC (read-only) and show
            // the verified check on the IBAN field. An empty BIC falls through to
            // the manual branch — locking an empty required field would dead-end
            // the form.
            this.bicControl.setValue(response.bankData.bic);
            this.bankName.set(response.bankData.name);
            this.bankCode.set(response.bankData.bankCode);
            this.bicReadonly.set(true);
            this.ibanVerified.set(true);
          } else {
            // IBAN valid but no bank data returned → let the user fill it in.
            this.bicControl.reset('', { emitEvent: false });
            this.bicControl.setErrors({ fetchError: true });
            this.bankName.set(null);
            this.bankCode.set(null);
            this.bicReadonly.set(false);
            this.ibanVerified.set(false);
          }
        },
        error: () => {
          this.bicVisible.set(true);
          this.bicControl.reset('', { emitEvent: false });
          this.bicControl.setErrors({ fetchError: true });
          this.bankName.set(null);
          this.bankCode.set(null);
          this.bicReadonly.set(false);
          this.ibanVerified.set(false);
        },
      });
  }

  onIbanBlur() {
    if (this.ibanControl.valid) {
      const formattedIban = this.formatIBAN(this.ibanControl.value);
      if (formattedIban !== this.ibanControl.value) {
        this.ibanControl.setValue(formattedIban, { emitEvent: false });
        // Silent setValue: write the normalised form through, or a lowercase IBAN is stored as typed.
        // Not a user edit (typing already marked it; tabbing past stored data must not).
        this.field.formControl.setValue({ iban: formattedIban, bic: this.bicControl.value });
      }
    }
  }

  private updateFormValue() {
    const value = {
      iban: this.ibanControl.value,
      bic: this.bicControl.value,
    };
    // Only reached from user input on the inner iban/bic controls (the ngOnInit hydration above
    // patches before this subscription exists), so the entry control is genuinely edited (#21026).
    commitUserValue(this.field.formControl, value);
  }

  private fetchBIC(iban: string) {
    return this.http.get<OpenIbanResponse>(`https://openiban.com/validate/${iban}?getBIC=true`).pipe(
      catchError(() => {
        return of(null);
      })
    );
  }

  private bicValidator() {
    return (control: FormControl) => {
      const value = control.value?.replace(/\s/g, '') || '';

      if (!value) {
        return null;
      }

      // BIC format: 8 or 11 characters
      // First 4: Bank code (letters)
      // Next 2: Country code (letters)
      // Next 2: Location code (letters or digits)
      // Last 3: Branch code (optional) (letters or digits)
      const bicRegex = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/;

      return bicRegex.test(value) ? null : { invalidBic: true };
    };
  }

  private formatIBAN(iban: string): string {
    return (
      iban
        .replace(/\s/g, '')
        .toUpperCase()
        .match(/.{1,4}/g)
        ?.join(' ') || iban
    );
  }
}
